// OPERATOR-CONSOLE: one versioned provisioning setting as a card — its label and (i), the version
// in force at the chosen scope and where the value comes from, the value, an Edit modal (a new
// version, with a note), Clear override at a company or satellite scope, and History with a hover
// Rollback (confirmed). The scope is the page's (`?company=` / `?satellite=`), picked on the card.

import { SETTING_SCOPES } from "@companyos/schema/contracts";
import type { OperationOutput } from "@companyos/sdk";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  FieldError,
  InfoTip,
  NativeSelect,
  NativeSelectOptGroup,
  NativeSelectOption,
  Notice,
  QuickAddDialog,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@companyos/ui";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { EraserIcon, HistoryIcon, PencilIcon, Undo2Icon } from "lucide-react";
import { createContext, type ReactNode, useContext, useState } from "react";
import type { Api } from "../../../api/client";
import { formatDateTime } from "../../format";
import { problemText } from "../../ops-parts";
import { DENSE_TABLE, RowAction, RowActions } from "../../settings/row-actions";
import { flatten, type Obj, TextField } from "./fields";
import { type EditedKey, KINDS, type KindCtx, type Named, SourceBadge } from "./kinds";

export type Detail = OperationOutput<"operator.settings.get">;
type Scope = Detail["scope"];

export type SettingsScope = {
  company: string | null;
  satellite: string | null;
  setScope: (next: { company: string | null; satellite: string | null }) => void;
  companies: readonly Named[];
  satellites: readonly Named[];
};
export const ScopeContext = createContext<SettingsScope>({
  company: null,
  satellite: null,
  setScope: () => {},
  companies: [],
  satellites: [],
});
export const useSettingsScope = () => useContext(ScopeContext);

export const settingKey = (key: string, company: string | null, satellite: string | null) =>
  ["operator", "settings", key, company ?? "", satellite ?? ""] as const;
export const settingQuery = (
  api: Api,
  key: Detail["key"],
  company: string | null,
  satellite: string | null,
) =>
  queryOptions({
    queryKey: settingKey(key, company, satellite),
    queryFn: () =>
      api.client.operator.settings.get({
        params: { key },
        query: {
          ...(company ? { company_id: company } : {}),
          ...(satellite ? { satellite_id: satellite } : {}),
        },
      }),
  });

/** The scope a key reads at on this page (a locked one wins). */
export function scopeOf(
  key: EditedKey,
  s: { company: string | null; satellite: string | null },
  lock?: Scope,
): { scope: Scope; company: string | null; satellite: string | null } {
  const scopes = SETTING_SCOPES[key];
  const want: Scope =
    lock ??
    (s.satellite && scopes.includes("satellite")
      ? "satellite"
      : s.company && scopes.includes("company")
        ? "company"
        : "platform");
  return {
    scope: want,
    company: want === "company" ? s.company : null,
    satellite: want === "satellite" ? s.satellite : null,
  };
}

const SCOPE_LABEL: Record<Scope, string> = {
  platform: "Platform",
  company: "Company",
  satellite: "Satellite",
};

/** Platform, a company or a satellite: the scopes this key takes. */
export function ScopePicker({
  scopes,
  value,
  label = "Scope",
}: {
  scopes: readonly Scope[];
  value: { scope: Scope; company: string | null; satellite: string | null };
  label?: string;
}) {
  const s = useSettingsScope();
  const current =
    value.scope === "company" && value.company
      ? `company:${value.company}`
      : value.scope === "satellite" && value.satellite
        ? `satellite:${value.satellite}`
        : "platform";
  return (
    <NativeSelect
      aria-label={label}
      size="sm"
      value={current}
      onChange={(e) => {
        const [kind, id] = e.target.value.split(":");
        s.setScope({
          company: kind === "company" ? (id ?? null) : null,
          satellite: kind === "satellite" ? (id ?? null) : null,
        });
      }}
    >
      {scopes.includes("platform") ? (
        <NativeSelectOption value="platform">Platform</NativeSelectOption>
      ) : null}
      {scopes.includes("company") && s.companies.length > 0 ? (
        <NativeSelectOptGroup label="Companies">
          {s.companies.map((c) => (
            <NativeSelectOption key={c.id} value={`company:${c.id}`}>
              {c.name}
            </NativeSelectOption>
          ))}
        </NativeSelectOptGroup>
      ) : null}
      {scopes.includes("satellite") && s.satellites.length > 0 ? (
        <NativeSelectOptGroup label="Satellites">
          {s.satellites.map((c) => (
            <NativeSelectOption key={c.id} value={`satellite:${c.id}`}>
              {c.name}
            </NativeSelectOption>
          ))}
        </NativeSelectOptGroup>
      ) : null}
    </NativeSelect>
  );
}

export function SettingCard({
  api,
  settingKey: key,
  lock,
  actions,
  label,
}: {
  api: Api;
  settingKey: EditedKey;
  /** A fixed scope (no picker): the Satellites section's overrides. */
  lock?: Scope;
  /** Extra header actions (Apply baseline). */
  actions?: ReactNode;
  label?: string;
}) {
  const s = useSettingsScope();
  const at = scopeOf(key, s, lock);
  const kind = KINDS[key];
  const name = label ?? kind.label;
  const q = useQuery(settingQuery(api, key, at.company, at.satellite));
  const [editing, setEditing] = useState(false);
  const [history, setHistory] = useState(false);
  const scopes = SETTING_SCOPES[key];
  const d = q.data;
  const ctx: KindCtx = { companies: s.companies, satellites: s.satellites, scope: at.scope };
  const missingId = at.scope !== "platform" && !at.company && !at.satellite;
  return (
    <section
      className="flex min-w-0 flex-col gap-3 rounded-lg border border-border bg-card p-4"
      data-slot="setting-card"
      data-key={key}
      aria-label={name}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <h2 className="flex min-w-0 flex-wrap items-center gap-2 text-sm font-semibold">
          {name}
          <InfoTip label={`About ${name.toLowerCase()}`}>{kind.info}</InfoTip>
          {d ? (
            <>
              {d.version ? (
                <Badge variant="accent" data-slot="setting-version">
                  v{d.version}
                </Badge>
              ) : null}
              <SourceBadge source={d.source} />
            </>
          ) : null}
        </h2>
        <div className="flex shrink-0 flex-wrap items-center gap-1">
          {scopes.length > 1 && !lock ? <ScopePicker scopes={scopes} value={at} /> : null}
          {lock ? <Badge variant="outline">{SCOPE_LABEL[lock]}</Badge> : null}
          {d?.editable ? (
            <Button variant="secondary" size="xs" onClick={() => setEditing(true)}>
              <PencilIcon aria-hidden /> Edit
            </Button>
          ) : null}
          {d && d.scope !== "platform" && d.override !== null ? (
            <ClearOverride api={api} detail={d} name={name} />
          ) : null}
          {d && d.history.length > 0 ? (
            <Button
              variant="ghost"
              size="xs"
              aria-expanded={history}
              onClick={() => setHistory((h) => !h)}
            >
              <HistoryIcon aria-hidden /> History
            </Button>
          ) : null}
          {actions}
        </div>
      </div>
      {missingId ? (
        <p className="text-xs text-muted-foreground">
          Pick a {SCOPE_LABEL[at.scope].toLowerCase()}.
        </p>
      ) : q.isPending ? (
        <p className="text-xs text-muted-foreground">Loading…</p>
      ) : q.isError ? (
        <Notice tone="danger">{problemText(q.error, `${name} could not be loaded.`)}</Notice>
      ) : d ? (
        <>
          <div className="min-w-0" data-slot="setting-value">
            <kind.View value={d.effective} ctx={ctx} />
          </div>
          {history ? <History api={api} detail={d} name={name} /> : null}
          {editing ? (
            <EditDialog
              api={api}
              detail={d}
              name={name}
              ctx={ctx}
              onClose={() => setEditing(false)}
            />
          ) : null}
        </>
      ) : null}
    </section>
  );
}

/** After a write: the card's own query, and what is rendered from settings. */
function useSaved(api: Api) {
  const qc = useQueryClient();
  void api;
  return (next: Detail) => {
    qc.setQueryData(
      settingKey(
        next.key,
        next.scope === "company" ? next.scope_id : null,
        next.scope === "satellite" ? next.scope_id : null,
      ),
      next,
    );
    void qc.invalidateQueries({ queryKey: ["operator", "settings"] });
    void qc.invalidateQueries({ queryKey: ["operator", "baseline"] });
    void qc.invalidateQueries({ queryKey: ["operator", "roster"] });
  };
}

const scopeBody = (d: Detail) => ({
  ...(d.scope === "company" && d.scope_id ? { company_id: d.scope_id } : {}),
  ...(d.scope === "satellite" && d.scope_id ? { satellite_id: d.scope_id } : {}),
});

function EditDialog({
  api,
  detail,
  name,
  ctx,
  onClose,
}: {
  api: Api;
  detail: Detail;
  name: string;
  ctx: KindCtx;
  onClose: () => void;
}) {
  const kind = KINDS[detail.key as EditedKey];
  const saved = useSaved(api);
  const start = (detail.override ?? detail.effective ?? detail.default) as Obj;
  const [draft, setDraft] = useState<Obj>(() => structuredClone(start));
  const [note, setNote] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () =>
      api.client.operator.settings.put({
        params: { key: detail.key },
        body: { ...scopeBody(detail), value: draft, ...(note ? { note } : {}) },
      }),
    onSuccess: (next) => {
      saved(next);
      onClose();
    },
  });
  const complete = flatten(draft).every(([, v]) => typeof v !== "number" || Number.isFinite(v));
  return (
    <QuickAddDialog
      title={`Edit ${name.toLowerCase()}`}
      info={kind.info}
      submitLabel="Save version"
      size={kind.wide ? "lg" : "default"}
      className="max-h-[90vh] overflow-y-auto"
      pending={save.isPending}
      canSubmit={complete}
      onSubmit={() => save.mutate()}
      onClose={onClose}
      error={save.error ? <FieldError>{problemText(save.error, "Not saved.")}</FieldError> : null}
      slot="setting-dialog"
    >
      <div className="grid min-w-0 gap-4">
        <kind.Form draft={draft} set={setDraft} ctx={ctx} />
        <TextField label="Note" nullable placeholder="Why" value={note} onChange={setNote} />
      </div>
    </QuickAddDialog>
  );
}

function ClearOverride({ api, detail, name }: { api: Api; detail: Detail; name: string }) {
  const [open, setOpen] = useState(false);
  const saved = useSaved(api);
  const clear = useMutation({
    mutationFn: () =>
      api.client.operator.settings.put({
        params: { key: detail.key },
        body: { ...scopeBody(detail), value: null },
      }),
    onSuccess: (next) => {
      saved(next);
      setOpen(false);
    },
  });
  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <Button variant="ghost" size="xs" onClick={() => setOpen(true)}>
        <EraserIcon aria-hidden /> Clear override
      </Button>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Clear the {name.toLowerCase()} override?</AlertDialogTitle>
          <AlertDialogDescription>
            The {detail.scope} goes back to the platform value. History keeps this version.
          </AlertDialogDescription>
        </AlertDialogHeader>
        {clear.isError ? <FieldError>{problemText(clear.error, "Not cleared.")}</FieldError> : null}
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <Button variant="destructive" disabled={clear.isPending} onClick={() => clear.mutate()}>
            {clear.isPending ? "Clearing…" : "Clear"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function History({ api, detail, name }: { api: Api; detail: Detail; name: string }) {
  const [target, setTarget] = useState<number | null>(null);
  const saved = useSaved(api);
  const rollback = useMutation({
    mutationFn: (version: number) =>
      api.client.operator.settings.rollback({
        params: { key: detail.key },
        body: { ...scopeBody(detail), version },
      }),
    onSuccess: (next) => {
      saved(next);
      setTarget(null);
    },
  });
  return (
    <div className="border-t border-border pt-3" data-slot="setting-history">
      <Table className={DENSE_TABLE}>
        <TableHeader>
          <TableRow>
            <TableHead scope="col">Version</TableHead>
            <TableHead scope="col">When</TableHead>
            <TableHead scope="col">Note</TableHead>
            <TableHead scope="col">
              <span className="sr-only">Actions</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {detail.history.map((h) => (
            <TableRow key={h.id} data-version={h.version} className="group/row">
              <TableHead scope="row" className="font-normal">
                <span className="inline-flex items-center gap-1.5">
                  v{h.version}
                  {h.current ? <Badge variant="success">Current</Badge> : null}
                  {h.value === null ? <Badge variant="outline">Cleared</Badge> : null}
                </span>
              </TableHead>
              <TableCell className="whitespace-nowrap tabular-nums">
                {formatDateTime(h.created_at)}
              </TableCell>
              <TableCell className="min-w-0 break-words text-muted-foreground">
                {h.note ?? "—"}
              </TableCell>
              <TableCell className="w-px text-right">
                {h.current || !detail.editable ? null : (
                  <RowActions>
                    <RowAction
                      icon={Undo2Icon}
                      label={`Roll back to v${h.version}`}
                      tip="Roll back"
                      onClick={() => setTarget(h.version)}
                    />
                  </RowActions>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <AlertDialog open={target !== null} onOpenChange={(o) => (o ? null : setTarget(null))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Roll {name.toLowerCase()} back to v{target}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              v{target} applies again. Later versions stay in History.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {rollback.isError ? (
            <FieldError>{problemText(rollback.error, "Not rolled back.")}</FieldError>
          ) : null}
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button
              disabled={rollback.isPending || target === null}
              onClick={() => target !== null && rollback.mutate(target)}
            >
              {rollback.isPending ? "Rolling back…" : "Roll back"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
